import classNames from 'classnames';
import dayjs from 'dayjs';
import { filter, some } from 'lodash';
import { useCallback, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Link, useHistory } from 'react-router-dom';
// import dayjs from 'dayjs';

import LibraryFeedPref from '../../../app/libraryAd/components/BadgeLibraryAdFeedPref';
import LibraryCalendar from '../../../app/libraryAd/components/LibraryCalendar';
import LibraryForm from '../../../app/libraryAd/components/LibraryForm';
import useFormSections from '../../../app/libraryAd/hooks/useFormSections';
import PreviewLibraryAdModal from '../../../app/libraryAd/modals/PreviewLibraryAdModal';
import SelectSectionModal from '../../../app/libraryAd/modals/SelectSectionModal';
import { FeedPref, TemplatePlacement } from '../../../core/graphql/types';
import useMutateUpsertTemplate from '../../../hooks/useMutateUpsertTemplate';
import useQueryTemplateByQuery from '../../../hooks/useQueryTemplateByQuery';
import { useQueryString } from '../../../utils/router';

const EditLibraryAdPage = (): JSX.Element => {
  const queryString = useQueryString();
  const history = useHistory();
  const pref = queryString.get('pref') as FeedPref;

  const [isOpenSelectModal, setIsOpenSelectModal] = useState<boolean>(false);
  const [isNCPreview, setIsNCPreview] = useState<boolean | undefined>(undefined);

  const onOpenSelectSectionModal = useCallback(() => {
    setIsOpenSelectModal(true);
  }, []);

  const onCloseSelectSectionModal = useCallback(() => {
    setIsOpenSelectModal(false);
  }, []);

  const { templateByQuery, isLoadingTemplateByQuery } = useQueryTemplateByQuery({
    placement: TemplatePlacement.Library,
    pref,
  });
  const { mutateUpsertTemplate, isLoadingMutateUpsertTemplate } = useMutateUpsertTemplate();

  const { formSections, onEditSection, onCreateSection, onDeleteSection, onUpdateScheduleInSection, onDeleteScheduleInSection, onCreateScheduleInSection } = useFormSections({
    templateByQuery,
  });

  // preview modal state
  const [previewDate, setPreviewDate] = useState<Date>(dayjs().startOf('day').toDate());
  const [previewSectionId, setPreviewSectionId] = useState<string | undefined>(undefined);

  const onOpenModalPreview = useCallback(
    (sectionId: string) => {
      setPreviewSectionId(sectionId);
    },
    [setPreviewSectionId],
  );

  const onOpenModalPreviewOnDate = useCallback(() => {
    const sectionsOnDate = filter(
      formSections,
      (section) =>
        section.isActivated &&
        (isNCPreview === undefined || section.isNCContent === isNCPreview) &&
        ((section.schedules ?? []).length === 0 ||
          some(section.schedules, (schedule) => dayjs(previewDate).startOf('day').isBetween(dayjs(schedule.start).add(-1, 'day'), dayjs(schedule.end)))),
    );

    if (sectionsOnDate.length) setPreviewSectionId(sectionsOnDate[0]._id);
  }, [formSections, isNCPreview, previewDate]);

  if (!pref) {
    return <h1>not found</h1>;
  }

  return (
    <div>
      <div className="breadcrumbs">
        <ul>
          <li>
            <Link to="/suggestProducts/libraryAd">
              <h1 className="py-5 font-dbh text-[28px]">Library Ad</h1>
            </Link>
          </li>
          <li>
            <LibraryFeedPref pref={pref} />
          </li>
        </ul>
      </div>
      <div />

      {isLoadingTemplateByQuery && <div className="w-full btn btn-outline btn-disabled bg-componentsBgGrouped02 border-none loading h-[100px]" />}

      {!isLoadingTemplateByQuery && (
        <>
          <div className="flex flex-col p-16 xl:p-32 xl:flex-row bg-componentsBgGrouped02">
            <div className="w-full pb-32 xl:w-2/5">
              {/* end user preview modal */}
              <div className="flex flex-col justify-start pb-16 space-x-0 space-y-24 sm:space-x-24 sm:items-end sm:flex-row">
                <div className="flex flex-col">
                  <div className="mr-8 label">Preview:</div>
                  <div className="btn-group">
                    <button
                      className={classNames('btn btn-sm', { 'bg-colorsBrandOrange01Primary text-white': isNCPreview === undefined })}
                      onClick={() => setIsNCPreview(undefined)}
                    >
                      All
                    </button>
                    <button className={classNames('btn btn-sm', { 'bg-colorsBrandOrange01Primary text-white': isNCPreview === false })} onClick={() => setIsNCPreview(false)}>
                      No NC
                    </button>
                  </div>
                </div>
                <div className="flex relative w-[250px]">
                  <DatePicker
                    selected={previewDate}
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => {
                      if (date instanceof Date) {
                        setPreviewDate(date);
                      }
                    }}
                  />
                  <button className="absolute top-0 right-0 h-full rounded-l-none btn btn-sm" onClick={onOpenModalPreviewOnDate}>
                    Preview
                  </button>
                </div>
              </div>

              {/* dynamic section list */}
              <div className="pr-0 xl:pr-32">
                <div className="divider opacity-10" />
                <LibraryForm
                  formSections={formSections}
                  onEditSection={onEditSection}
                  onDeleteSection={onDeleteSection}
                  onUpdateScheduleInSection={onUpdateScheduleInSection}
                  onCreateScheduleInSection={onCreateScheduleInSection}
                  onDeleteScheduleInSection={onDeleteScheduleInSection}
                  onOpenModalPreview={onOpenModalPreview}
                />
                <div className="flex items-center justify-center">
                  <button onClick={onOpenSelectSectionModal} className="btn btn-outline">
                    Select sections
                  </button>
                </div>
              </div>
            </div>

            {/* all section calendar */}
            <div className="xl:sticky top-0 h-[fit-content] h-600 xl:w-3/5">
              <LibraryCalendar formSections={formSections} onUpdateScheduleInSection={onUpdateScheduleInSection} onDeleteScheduleInSection={onDeleteScheduleInSection} />
            </div>
          </div>

          {/* submit form */}
          <div className="flex items-center justify-center p-32">
            <button
              className={classNames('btn btn-md', {
                '!bg-systemGrays02LabelSecondary': isLoadingMutateUpsertTemplate,
              })}
              disabled={isLoadingMutateUpsertTemplate}
              onClick={async () => {
                const result = await mutateUpsertTemplate({
                  variables: {
                    input: {
                      pref,
                      placement: TemplatePlacement.Library,
                      isActivated: true,
                      sections: formSections.map((section) => {
                        return {
                          _id: section._id!,
                          note: section.note,
                          schedules: section.schedules?.map((schedule) => ({
                            startedAt: dayjs(schedule.start).startOf('day').toISOString(),
                            endedAt: dayjs(schedule.end).endOf('day').toISOString(),
                          })),
                        };
                      }),
                    },
                  },
                });
                if (result.data?.upsertTemplate) {
                  history.push('/suggestProducts/libraryAd');
                }
              }}
            >
              {isLoadingMutateUpsertTemplate && <div className="text-white">Loading...</div>}
              {!isLoadingMutateUpsertTemplate && 'Submit'}
            </button>
          </div>

          {/* modals */}
          {isOpenSelectModal && (
            <SelectSectionModal onClose={onCloseSelectSectionModal} formSections={formSections} onCreateSection={onCreateSection} onDeleteSection={onDeleteSection} />
          )}

          {previewSectionId && <PreviewLibraryAdModal sectionId={previewSectionId} onClose={() => setPreviewSectionId(undefined)} />}
        </>
      )}
    </div>
  );
};

export default EditLibraryAdPage;
